import { useState, type ReactNode } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormLayout } from '@nucleus-commerce/admin-kit/forms';
import type { Product, Variant } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { session } from '@/shared/session/session';

import { useProductEditor, type ProductSubmit } from '../hooks/use-product-editor';
import { ProductScopeProvider } from '../hooks/use-product-scope';
import type { ProductFormInput } from '../schemas/product-form-schema';

import { AttributeValuesSection } from './attribute-values-section';
import { BasicsSection } from './basics-section';
import { CustomizationsSection } from './customizations-section';
import { MetadataFields } from './metadata-fields';
import { OptionsSection } from './options-section';
import { ProductSeoSection } from './product-seo-section';
import {
  ProductFulfilmentSection,
  ProductOrganisationSection,
  ProductStatusCard,
} from './product-side-rail';
import { ShippingSection } from './shipping-section';

export const PRODUCT_FORM_ID = 'product-form';

export interface ProductFormProps {
  defaultValues: ProductFormInput;
  onSubmit: ProductSubmit;
  isSubmitting: boolean;
  mode: 'create' | 'edit';
  /** Existing variants, so the Options card can say what saving would do to them. */
  variants?: readonly Variant[] | undefined;
  /** The featured image, for the SEO preview's image warnings; undefined before the first save. */
  featuredImage?: { alt: string | null } | null | undefined;
  /**
   * The saved product, so the Options card can compare against it and the style number field
   * can say where its value came from; undefined on create.
   */
  saved?: Product | undefined;
  /** The form's values after a save, from what `onSubmit` resolved with. */
  reseed?: (saved: unknown) => ProductFormInput | undefined;
  /** Above both columns, inside the form's context: the page's header. */
  header?: ReactNode;
  /** The gallery, rendered right under the basics; it saves on its own, outside the <form>. */
  media?: ReactNode;
  /** Main column under the options: variants, configurator, relations; on create, the note. */
  children?: ReactNode;
  /** Rail, between customizations and fulfilment. */
  collections?: ReactNode;
  /** Rail, under shipping. */
  aside?: ReactNode;
}

/**
 * The create page has no search card, and the handle follows the title out of sight. When the
 * handle is what stops a save (taken, or a title that gives none) its input appears here, and stays.
 */
function CreateHandleField() {
  const { t } = useTranslation('products');
  const form = useFormContext<ProductFormInput>();
  const { errors } = useFormState({ control: form.control, name: 'handle' });
  const [shown, setShown] = useState(false);
  if (!shown && errors.handle) setShown(true);
  if (!shown) return null;
  return (
    <Card className="py-4">
      <CardContent className="px-4">
        <FormField
          control={form.control}
          name="handle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.handle')}</FormLabel>
              <FormControl>
                <Input className="font-mono text-[13px]" {...field} />
              </FormControl>
              <FormDescription>{t('masterPage.createHandleHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}

/**
 * The product editor: two columns, main cards split over several <form>s that submit through the
 * same handler, the rail bound to the same react-hook-form context, and any non-form sections
 * (media, variants, configurator, relations) as siblings so their inputs never submit the product.
 */
export function ProductForm({
  defaultValues,
  onSubmit,
  isSubmitting,
  mode,
  variants,
  featuredImage,
  saved,
  reseed,
  header,
  media,
  children,
  collections,
  aside,
}: ProductFormProps) {
  const { t } = useTranslation(['products', 'errors']);
  const { me } = session.useSession();
  const editing = mode === 'edit';
  const {
    form,
    submit,
    attributes,
    attributesById,
    productTypes,
    productType,
    productTypeLoading,
    rootError,
    readOnly,
  } = useProductEditor({
    defaultValues,
    onSubmit,
    isSubmitting,
    reseed,
    savedSkuRoot: saved === undefined ? undefined : saved.skuRoot,
  });
  // Someone who may only read gets the store page's value-only cards. No `productId`: there is no
  // master to link to from the master page.
  const formCard = (card: ReactNode) => (
    <ProductScopeProvider value={{ readOnly }}>{card}</ProductScopeProvider>
  );
  const productTypeId = form.watch('productTypeId');
  const metadata = (
    <form onSubmit={submit} noValidate>
      <MetadataFields />
    </form>
  );

  return (
    <Form {...form}>
      {header}
      <FormLayout
        aside={
          <>
            {formCard(
              <>
                <ProductStatusCard editing={editing} />
                <ProductOrganisationSection
                  productTypes={productTypes}
                  saved={saved ?? null}
                  editing={editing}
                />
                <div data-tour={editing ? 'catalog.product-edit.customizations' : undefined}>
                  <CustomizationsSection />
                </div>
              </>,
            )}
            {collections}
            {formCard(
              <>
                <ProductFulfilmentSection editing={editing} />
                <ShippingSection />
              </>,
            )}
            {aside}
          </>
        }
      >
        <form id={PRODUCT_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertTitle>{t('products:saveFailed')}</AlertTitle>
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <div data-tour={editing ? 'catalog.product-edit.details' : 'catalog.product-new.details'}>
            {formCard(<BasicsSection handleFollowsTitle={!editing} />)}
          </div>
          {editing ? null : <CreateHandleField />}
        </form>
        {media}
        <form
          onSubmit={submit}
          noValidate
          data-tour={editing ? 'catalog.product-edit.attributes' : undefined}
        >
          {formCard(
            <AttributeValuesSection
              productType={productType}
              attributesById={attributesById}
              isLoading={productTypeLoading}
            />,
          )}
        </form>
        <form
          onSubmit={submit}
          noValidate
          data-tour={editing ? 'catalog.product-edit.options' : 'catalog.product-new.options'}
        >
          {formCard(
            <OptionsSection
              attributes={attributes}
              attributesById={attributesById}
              saved={saved}
              variants={variants}
            />,
          )}
        </form>
        {editing ? null : metadata}
        {children}
        {editing ? (
          <>
            <form onSubmit={submit} noValidate data-tour="catalog.product-edit.seo-preview">
              {formCard(
                <ProductSeoSection
                  organisationName={me?.organization.name}
                  hasImage={featuredImage != null}
                  imageAlt={featuredImage?.alt ?? null}
                  productType={productTypeId ? (productType ?? null) : null}
                />,
              )}
            </form>
            {metadata}
          </>
        ) : null}
      </FormLayout>
    </Form>
  );
}
